import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Heart, Sparkle, CircleNotch, ShieldCheck } from "@phosphor-icons/react";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { daysLeft } from "../components/Paywall";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const PERKS = [
  "Post anonymously in every channel",
  "Reply and support others with comments",
  "Daily mood check-ins & streaks",
  "Hug, support & heart reactions",
  "Help keep this space safe & ad-free",
];

export default function Subscribe() {
  const { sub, refreshSub } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);

  const sessionId = params.get("session_id");

  const poll = useCallback(async (sid, attempt = 0) => {
    if (attempt >= 6) {
      setPolling(false);
      toast.error("Still processing. Refresh in a moment to see your membership.");
      return;
    }
    try {
      const res = await api.get(`/subscription/checkout/status/${sid}`);
      if (res.data.payment_status === "paid") {
        await refreshSub();
        setPolling(false);
        toast.success("Welcome, member! 🌿 You're all set.");
        setParams({}, { replace: true });
        return;
      }
      if (res.data.status === "expired") {
        setPolling(false);
        toast.error("That checkout expired. Please try again.");
        setParams({}, { replace: true });
        return;
      }
      setTimeout(() => poll(sid, attempt + 1), 2000);
    } catch {
      setTimeout(() => poll(sid, attempt + 1), 2000);
    }
  }, [refreshSub, setParams]);

  useEffect(() => {
    if (sessionId) {
      setPolling(true);
      poll(sessionId);
    }
  }, [sessionId, poll]);

  const subscribe = async () => {
    setLoading(true);
    try {
      const res = await api.post("/subscription/checkout", { origin_url: window.location.origin });
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("no url");
      }
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      toast.error(detail && detail !== "Something went wrong. Please try again." ? detail : "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const isMember = sub && sub.status === "active";
  const onTrial = sub && sub.status === "trial";

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="max-w-xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {polling ? (
          <div className="text-center py-20" data-testid="subscribe-polling">
            <CircleNotch size={48} weight="bold" color="#8A9A86" className="mx-auto mb-4 animate-spin" />
            <p className="font-heading text-xl text-[#5E6C5C]">Confirming your membership…</p>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 mb-2">
              <Heart size={22} weight="duotone" color="#D97B6A" />
              <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#8A9A86]">Membership</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#2C362B] tracking-tight mb-3">
              Support the space, keep sharing
            </h1>
            <p className="text-[#6D7A6A] text-lg mb-8 leading-relaxed">
              Reading is always free. A membership lets you post and reply — and helps keep this a calm, ad-free place for everyone.
            </p>

            <div className="bg-white rounded-[2rem] p-7 sm:p-9 border border-[#E6EBE4]/60 shadow-[0_8px_30px_rgba(44,54,43,0.03)]" data-testid="pricing-card">
              {isMember && (
                <div className="flex items-center gap-2 bg-[#E6EBE4] text-[#5E6C5C] rounded-full px-4 py-2 text-sm font-semibold mb-5 w-fit" data-testid="member-status">
                  <ShieldCheck size={18} weight="fill" /> You're a member · renews access until {new Date(sub.expires_at).toLocaleDateString()}
                </div>
              )}
              {onTrial && (
                <div className="flex items-center gap-2 bg-[#FDF0E6] text-[#D48A54] rounded-full px-4 py-2 text-sm font-semibold mb-5 w-fit" data-testid="trial-status">
                  <Sparkle size={18} weight="fill" /> Free trial · {daysLeft(sub.trial_ends_at)} days left
                </div>
              )}

              <div className="flex items-end gap-2 mb-1">
                <span className="font-heading text-5xl font-semibold text-[#2C362B]">$7.77</span>
                <span className="text-[#6D7A6A] mb-2">/ month</span>
              </div>
              <p className="text-sm text-[#8A9A86] mb-6">Starts with a 7-day free trial. Cancel anytime.</p>

              <ul className="space-y-3 mb-8">
                {PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-[#515A4F]">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#E6EBE4] flex items-center justify-center shrink-0">
                      <Check size={13} weight="bold" color="#5E6C5C" />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>

              <button
                onClick={subscribe}
                disabled={loading || isMember}
                className="w-full bg-[#8A9A86] text-white hover:bg-[#6D7A6A] rounded-full px-8 py-4 font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                data-testid="subscribe-checkout-btn"
              >
                {isMember ? "You're already a member" : loading ? "Opening secure checkout…" : onTrial ? "Become a member now" : "Start membership"}
              </button>
              {isMember && (
                <p className="text-xs text-[#A8B0A3] text-center mt-4">
                  Renewing again extends your access by another 30 days.
                </p>
              )}
            </div>

            <button onClick={() => navigate("/")} className="mt-6 text-sm font-semibold text-[#6D7A6A] hover:underline mx-auto block" data-testid="subscribe-back-btn">
              ← Back to the feed
            </button>
          </motion.div>
        )}
      </main>
    </div>
  );
}
